import { PermissionType } from '@apply/constants';
import moment from 'moment-timezone';
import store from '@apply/adminStore';

const centralTime = (date: Date | string) => {
    return moment.utc(date).tz('America/Chicago');
};

export const formatDateTime = (date: Date | string): string => {
    const central = centralTime(date);
    return `${central.format('HH:mm')} CT ${central.format('MM/DD/YYYY')}`;
};

export const formatDate = (date: Date | string): string => {
    return `${centralTime(date).format('YYYY-MM-DD')}`;
};

export const formatDateMonthDayYear = (date: Date): string => {
    return `${centralTime(date).format('MM-DD-YYYY')}`;
};

export const formatDateLocal = (date: Date): string => {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return [mm, dd, yyyy].join('/');
};

export const formatToSqlDateTime = (date: Date) => {
    const pad = (n: number) => n.toString().padStart(2, '0');

    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const formatMdd = (input: Date | string | undefined): string => {
    if (!input) return '';
    const date = typeof input === 'string' ? new Date(input) : input;
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        return '';
    }
    const m = date.getMonth() + 1;
    const d = String(date.getDate()).padStart(2, '0');
    return `${m}/${d}`;
};

export const MMDDYYYY = /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])\/\d{4}$/;

export const isDate = (val: unknown): val is Date => {
    return Object.prototype.toString.call(val) === '[object Date]';
};

export const validateDateString = (value: string): boolean => {
    return MMDDYYYY.test(value);
};

export const addDays = (date: Date, days: number): Date => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
};

export const formatCurrency = (value: number, decimals = 2): string => {
    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: decimals,
    });

    return formatter.format(value ?? 0);
};

export const formatCurrencyOrUndefined = (value: number, decimals = 2): string => {
    if (value || value === 0) {
        return formatCurrency(value, decimals);
    }

    return '';
};

export const isDateInFuture = (date: string | null): boolean => {
    if (!date) return false;

    // Only compare the calendar date. Stored values may carry a time portion
    // ('YYYY-MM-DD HH:mm:ss'), which would otherwise make today compare as "future".
    const acquisitionYearMonthDay = date.slice(0, 10).replace(/-/g, '');

    const today = new Date();
    const todayYearMonthDay = `${today.getFullYear()}${(today.getMonth() + 1)
        .toString()
        .padStart(2, '0')}${today.getDate().toString().padStart(2, '0')}`;

    return acquisitionYearMonthDay > todayYearMonthDay; // i.e. '20220619' > '20220630' (YYYYMMDD)
};

export const formatPercent = (val?: number, decimals = 2): string | null => {
    if (val === null || val === undefined || isNaN(val)) {
        return null;
    }
    return (val * 100).toFixed(decimals) + '%';
};

export const formatPhone = (phoneNumberString: string): string | null => {
    const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
        return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return null;
};

export const hasPermission = (role: PermissionType) => {
    const { bfiUser } = store.getState();

    if (!bfiUser || !bfiUser.roles) {
        return false;
    }
    return bfiUser.roles.includes(role);
};

export const isPhoneNumberValid = (num: string, required: boolean) => {
    if (!required && !num) {
        return true;
    }

    const rgx = /^\(\d{3}\) \d{3}-\d{4}$/;
    return rgx.test(num);
};

export const detectDeviceType = (): string => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /android/i.test(navigator.userAgent);

    if (isIOS) {
        return 'ios';
    } else if (isAndroid) {
        return 'android';
    } else {
        return 'other';
    }
};

// The 450px width is often used as a benchmark in web and app design
// because it closely aligns with the screen width of many smaller smartphones
// in portrait orientation. It serves as a practical breakpoint for designing
// responsive layouts, ensuring that content displays well on compact devices.
export const isMobileScreen = (): boolean => {
    return window.screen.width < 450;
};

export const detectScreenWidth = (): number => {
    return window.screen.width;
};
